/**
 * @file The ExchangeDetailComponent is used as detail view for the inbox and outbox to display exchange requests.
 * @author Tom Verschoore de la Houssaije
 */
import * as tslib_1 from "tslib";
import { OnDestroy } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { RoutesService, ROUTE_PARAM_ID } from '../../routes.service';
import { UserService } from '../../user.service';
import { AbstractView } from '../../base/helpers/abstract-view';
import { FormBase } from '../../base/form/form.base';
import { DialogService } from '../../core/dialog.service';
import { AlertsService } from '../../core/alerts.service';
import { DataSet } from '../../core/helpers/data-set';
import { PropertyFilter } from '../../core/helpers/property-filter';
import { FilterOperators } from '../../core/helpers/filter-operators';
import { NotificationsService } from '../notifications.service';
import { RequestStatus } from '../request-status';
import { SettingsService } from '../../settings.service';
/**
 * The ExchangeDetailComponent is used as detail view for the inbox and outbox to display exchange requests.
 * @export
 * @class ExchangeDetailComponent
 * @extends {AbstractView}
 */
var ExchangeDetailComponent = /** @class */ (function (_super) {
    tslib_1.__extends(ExchangeDetailComponent, _super);
    /**
     * Constructs a DetailComponent
     * @param configurationsService Service to get the configuration for this component.
     * @param router The angular router enabling navigation between the different form modes.
     * @param route The active route.
     * @param notificationsService The notifications service.
     * @param dialogService The dialog service.
     */
    function ExchangeDetailComponent(userService, router, route, notificationsService, dialogService, alertsService, settingsService, translateService) {
        var _this = _super.call(this, router, route, notificationsService, dialogService, alertsService, translateService) || this;
        _this.userService = userService;
        _this.notificationsService = notificationsService;
        _this.dialogService = dialogService;
        _this.alertsService = alertsService;
        _this.settingsService = settingsService;
        /**
         * Breeze EntityType Request.
         * @private
         * @type {EntityType}
         * @memberOf ExchangeDetailComponent
         */
        _this.entityType = null;
        /**
         * The notification request shown in detail.
         * @protected
         * @type {Request}
         * @memberOf ExchangeDetailComponent
         */
        _this.request = null;
        /**
         * The field property of the fieldset-container for the request's note.
         * @type {FieldConfiguration}
         * @memberOf ExchangeDetailComponent
         */
        _this.noteField = {
            name: 'Note',
            labelKey: 'APP.NOTIFICATIONS.NOTE',
            editor: 'text',
            readonly: true
        };
        /**
         * The field property of the fieldset-container for the request's status.
         * @type {FieldConfiguration}
         * @memberOf ExchangeDetailComponent
         */
        _this.statusField = {
            name: 'Status',
            labelKey: 'APP.NOTIFICATIONS.STATUS',
            editor: 'select',
            readonly: true,
            format: [
                { labelKey: 'APP.NOTIFICATIONS.STATUS_REQUEST', value: 'REQUEST' },
                { labelKey: 'APP.NOTIFICATIONS.STATUS_ACCEPT', value: 'ACCEPT' },
                { labelKey: 'APP.NOTIFICATIONS.STATUS_DECLINE', value: 'DECLINE' },
                { labelKey: 'APP.NOTIFICATIONS.STATUS_WITHDRAWN', value: 'WITHDRAWN' },
                { labelKey: 'APP.NOTIFICATIONS.STATUS_CONFLICT', value: 'CONFLICT' }
            ]
        };
        /**
         * The field property of the fieldset container for the request's expiration date.
         * @type {FieldConfiguration}
         * @memberOf ExchangeDetailComponent
         */
        _this.expirationField = {
            name: 'Expiration',
            labelKey: 'APP.NOTIFICATIONS.EXPIRES',
            editor: 'date',
            readonly: true
        };
        /**
         * The field property of the fieldset container for the request's recipients.
         * @type {FieldConfiguration}
         * @memberOf ExchangeDetailComponent
         */
        _this.readByField = {
            name: 'recipients',
            labelKey: 'APP.NOTIFICATIONS.READ_BY',
            editor: 'readby',
            readonly: true
        };
        /**
         * FormControl to bind with the message fieldset.
         * This is needed for the angular input validation mechanism.
         * The fieldset displays the note of the request.
         * @memberOf ExchangeDetailComponent
         */
        _this.noteControl = new FormControl();
        /**
         * FormControl to bind with the date fieldset.
         * This is needed for the angular input validation mechanism.
         * The fieldset displays the expiration date of the request.
         * @memberOf ExchangeDetailComponent
         */
        _this.expireControl = new FormControl();
        /**
         * FormControl to bind with the select fieldset.
         * This is needed for the angular input validation mechanism.
         * The fieldset displays the status of the request.
         * @memberOf ExchangeDetailComponent
         */
        _this.statusControl = new FormControl();
        /**
         * FormControl to bind with the read by fieldset.
         * This is needed for the angular input validation mechanism.
         * The fieldset displays the recipients that have read the request.
         * @memberOf ExchangeDetailComponent
         */
        _this.readByControl = new FormControl();
        /**
         * FormGroup that is needed for the angular input validation mechanism.
         * @memberOf ExchangeDetailComponent
         */
        _this.form = new FormGroup({});
        /**
         * The entityKey of the entity shown by this detail.
         * @memberOf ExchangeDetailComponent
         */
        _this.entityKey = undefined;
        /**
         * Gets the recipients that have read the request displayed.
         * @memberOf ExchangeDetailComponent
         */
        _this.readBy = {
            recipients: []
        };
        _this.form.addControl('note', _this.noteControl);
        _this.form.addControl('expireControl', _this.expireControl);
        _this.form.addControl('statusControl', _this.statusControl);
        _this.form.addControl('readByControl', _this.readByControl);
        return _this;
    }
    Object.defineProperty(ExchangeDetailComponent.prototype, "subjectOid", {
        /**
         * Gets the oid of the planned subject of the displayed request.
         * @readonly
         * @memberOf ExchangeDetailComponent
         */
        get: function () {
            if (!this.request) {
                return '';
            }
            return this.request.SubjectOid;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ExchangeDetailComponent.prototype, "targetOid", {
        /**
         * Gets the oid of the planned target of the displayed request.
         * @readonly
         * @memberOf ExchangeDetailComponent
         */
        get: function () {
            if (!this.request) {
                return '';
            }
            return this.request.TargetOid;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ExchangeDetailComponent.prototype, "isReadOnly", {
        /**
         * If an item is a state other than 'Request', the request is readonly.
         * @readonly
         * @type {boolean} whether or not the item is readonly or not
         * @memberOf ExchangeDetailComponent
         */
        get: function () {
            return this.request.ReadOnly;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ExchangeDetailComponent.prototype, "targetLabelKey", {
        /**
         * Returns a label indicating whether the request has been exchanged already or is still in proposal.
         * @readonly
         * @type {string}
         * @memberOf ExchangeDetailComponent
         */
        get: function () {
            if (this.request && this.request.Status === RequestStatus.Accept) {
                return 'APP.NOTIFICATIONS.EXCHANGED_FOR';
            }
            return 'APP.NOTIFICATIONS.PROPOSAL';
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ExchangeDetailComponent.prototype, "hasComments", {
        get: function () {
            if (this.settingsService.settings.comments !== undefined && this.settingsService.settings.comments === false) { // Fix for #4165
                return false;
            }
            if (!this.configuration || !this.configuration.features) {
                return false;
            }
            return this.configuration.features.findIndex(function (feature) { return feature === 'Comments'; }) > -1;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ExchangeDetailComponent.prototype, "hasFiles", {
        get: function () {
            if (this.settingsService.settings.files !== undefined && this.settingsService.settings.files === false) { // Fix for #4184
                return false;
            }
            if (!this.configuration || !this.configuration.features) {
                return false;
            }
            return this.configuration.features.findIndex(function (feature) { return feature === 'Files'; }) > -1;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Destroys the URL parameters subscription.
     * @memberOf ExchangeDetailComponent
     */
    ExchangeDetailComponent.prototype.ngOnDestroy = function () {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    };
    /**
     * Sets the request. This method is overridden by ExchangeDayDetailComponent.
     * @protected
     * @param {Request} value The request.
     * @memberOf ExchangeDetailComponent
     */
    ExchangeDetailComponent.prototype.setRequest = function (value) {
        this.request = value;
    };
    /**
     * Is called after the block name is set and the configuration is loaded.
     * Override this method to process the loaded configuration data.
     * @memberOf ExchangeDetailComponent
     */
    ExchangeDetailComponent.prototype.afterInitialize = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var _this = this;
            return tslib_1.__generator(this, function (_a) {
                this.entityType = this.notificationsService.getEntityType('Request');
                this.subscription = this.route.params.subscribe(function (params) { return tslib_1.__awaiter(_this, void 0, void 0, function () {
                    var dataConfig, request, components;
                    return tslib_1.__generator(this, function (_a) {
                        switch (_a.label) {
                            case 0:
                                this.entityKey = params[ROUTE_PARAM_ID];
                                dataConfig = this.configuration.data;
                                return [4 /*yield*/, this.notificationsService.getEntityByKey(this.entityType, this.entityKey, true, dataConfig.expand).toPromise()];
                            case 1:
                                request = _a.sent();
                                this.setRequest(request);
                                this.afterRequestRetrieved();
                                components = Object.create(this.configuration.components);
                                this.setComponentConfigurations(components);
                                return [2 /*return*/];
                        }
                    });
                }); });
                return [2 /*return*/];
            });
        });
    };
    /**
     * Sets the child components. This method is overridden in case of different component configurations.
     * @protected
     * @param {ChildComponent[]} components The child component configurations.
     * @memberOf ExchangeDetailComponent
     */
    ExchangeDetailComponent.prototype.setComponentConfigurations = function (components) {
        if (components.length === 3) {
            // Only in edit mode an insight exists.
            // TODO: Consider using components[0].component to decide if it is an insight or not.
            if (components.length > 0) {
                this.insights = components[0];
                components.splice(0, 1);
            }
        }
        if (components.length > 0) {
            this.subjectForm = components[0];
            components.splice(0, 1);
        }
        if (components.length > 0) {
            this.targetForm = components[0];
            components.splice(0, 1);
        }
    };
    /**
     * Is invoked after the request of the detail is retrieved.
     * @protected
     * @memberOf ExchangeDetailComponent
     */
    ExchangeDetailComponent.prototype.afterRequestRetrieved = function () {
        this.checkIsRead();
    };
    Object.defineProperty(ExchangeDetailComponent.prototype, "isOutbox", {
        /**
         * Determines the item currently shown is in the outbox
         * @readonly
         * @type {boolean}
         * @memberOf ExchangeDetailComponent
         */
        get: function () {
            if (!this.configuration || !this.configuration.features) {
                return false;
            }
            var outbox = this.configuration.features.find(function (item) { return item === 'Outbox'; });
            return !!outbox;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Closes the current detail screen
     * @memberOf ExchangeDetailComponent
     */
    ExchangeDetailComponent.prototype.close = function () {
        // Not forget query params.
        RoutesService.navigateBack(this.router, this.route, this.route.snapshot.queryParams);
    };
    /**
     * Accepts the currently displayed request
     * @param {any} event Event triggering this method
     * @memberOf ExchangeDetailComponent
     */
    ExchangeDetailComponent.prototype.acceptRequest = function (event) {
        var _this = this;
        this.notificationsService.acceptRequest(this.request, function (error) {
            if (error) {
                _this.notificationsService.rejectChanges();
            }
            else {
                _this.alertsService.createAlert('success', 'APP.FORMS.ITEMS_SUCCESSFULLY_SAVED');
            }
            if (event) {
                event.target.classList.remove('sym-working');
            }
        }, function (saveResult) {
            _this.close();
        });
    };
    /**
     * Declines the currently displayed request
     * @param {any} event Event triggering this method
     * @memberOf ExchangeDetailComponent
     */
    ExchangeDetailComponent.prototype.declineRequest = function (event) {
        var _this = this;
        this.notificationsService.declineRequest(this.request, function (error) {
            if (error) {
                _this.notificationsService.rejectChanges();
            }
            else {
                _this.alertsService.createAlert('success', 'APP.FORMS.ITEMS_SUCCESSFULLY_SAVED');
            }
            if (event) {
                event.target.classList.remove('sym-working');
            }
        }, function (saveResult) {
            _this.close();
        });
    };
    /**
     * Withdraws the currently displayed request
     * @param {any} event Event triggering this method
     * @memberOf ExchangeDetailComponent
     */
    ExchangeDetailComponent.prototype.withdrawRequest = function (event) {
        var _this = this;
        this.notificationsService.withDrawRequest(this.request, function (error) {
            if (error) {
                _this.notificationsService.rejectChanges();
            }
            else {
                _this.alertsService.createAlert('success', 'APP.FORMS.ITEMS_SUCCESSFULLY_SAVED');
            }
            if (event) {
                event.target.classList.remove('sym-working');
            }
        }, function (saveResult) {
            _this.close();
        });
    };
    /**
     * Returns a value indicating whether the request has been loaded.
     * @returns {boolean} True if the request has been loaded.
     * @memberOf ExchangeDetailComponent
     */
    ExchangeDetailComponent.prototype.canSave = function () {
        return this.request && this.targetFormObject && this.targetFormObject.item != null && this.subjectFormObject && this.subjectFormObject.item != null;
    };
    /**
     * Sets the request read flag to true for the current user.
     * @protected
     * @memberOf ExchangeDetailComponent
     */
    ExchangeDetailComponent.prototype.checkIsRead = function () {
        var _this = this;
        if (!this.configuration || !this.request) {
            return;
        }
        this.loadReadByRecipients(this.notificationsService.getEntityType('UserRequestStatus'));
        if (this.request.IsRead) {
            return;
        }
        var user = this.userService.user;
        if (!user) {
            return;
        }
        var requestStatus = this.request.UserRequestStatusItems.find(function (item) {
            return item.UserOid === _this.userService.user['Oid'];
        });
        if (!requestStatus) {
            console.warn('Item Oid does not match with User Oid, Probably the employee has changed');
            return;
        }
        if (requestStatus.RequestIsRead) {
            this.notificationsService.getEntityByKey(this.entityType, this.entityKey, false);
            this.loadReadByRecipients(requestStatus.entityType);
        }
        else {
            requestStatus.RequestIsRead = true;
            this.notificationsService.saveChanges(function (error) {
                // Give correct alert based on whether an error is given or not
                if (!error) {
                    _this.notificationsService.getEntityByKey(_this.entityType, _this.entityKey, false);
                    _this.loadReadByRecipients(requestStatus.entityType);
                }
            });
        }
        this.notificationsService.updateUnreadCount();
    };
    /**
     * Loads the recipients that have read the request.
     * @private
     * @param {EntityType} loadType The entity type to load.
     * @memberOf ExchangeDetailComponent
     */
    ExchangeDetailComponent.prototype.loadReadByRecipients = function (loadType) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var recipients, response;
            var _this = this;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        recipients = new DataSet();
                        recipients.queryOptions = { expand: 'Recipient' };
                        recipients.entityType = loadType;
                        recipients.filter = new PropertyFilter(recipients, 'RequestId', FilterOperators.Equals, this.request['Id']);
                        // tslint:disable-next-line:no-unused-expression
                        new PropertyFilter(recipients, 'RequestIsRead', FilterOperators.Equals, true, FilterOperators.And);
                        return [4 /*yield*/, this.notificationsService.getDataSet(recipients)];
                    case 1:
                        response = _a.sent();
                        this.readBy.recipients = [];
                        response.results.forEach(function (item) {
                            _this.readBy.recipients.push(item['Recipient']);
                        });
                        return [2 /*return*/];
                }
            });
        });
    };
    return ExchangeDetailComponent;
}(AbstractView));
export { ExchangeDetailComponent };
